import { Textarea } from '@/components/ui/shadcn/textarea'
import { FACEBOOK_RESTRICTIONS } from '@backend/lib/platforms/facebook/lib/restrictions'
import { INSTAGRAM_RESTRICTIONS } from '@backend/lib/platforms/instagram/lib/restrictions'
import { LINKEDIN_RESTRICTIONS } from '@backend/lib/platforms/linkedin/lib/restrictions'
import { TIKTOK_RESTRICTIONS } from '@backend/lib/platforms/tiktok/lib/restrictions'
import { YOUTUBE_RESTRICTIONS } from '@backend/lib/platforms/youtube/lib/restrictions'
import { Card } from '../ui/shadcn/card'
import { Check, Loader2Icon } from 'lucide-react'
import { type PlatformType } from '@backend/lib/db/schema/platform'

interface Props {
  textareaRef: React.RefObject<HTMLTextAreaElement | null> | null
  caption: string
  onCaptionChange: (value: string) => void
  isSaving: boolean
  selectedCaptionPlatform: PlatformType
  title?: string
  onTitleChange?: (value: string) => void
  minHeight?: string
  className?: string
  /** The platform's caption was published from this workspace — no more edits. */
  readOnly?: boolean
}

export function WorkspaceCaption({
  textareaRef,
  caption,
  onCaptionChange,
  isSaving,
  selectedCaptionPlatform,
  title,
  onTitleChange,
  minHeight = 'min-h-[200px]',
  readOnly = false,
}: Props) {
  const getMaxLength = () => {
    switch (selectedCaptionPlatform) {
      case 'instagram':
        return INSTAGRAM_RESTRICTIONS.caption.maxLength
      case 'linkedin':
        return LINKEDIN_RESTRICTIONS.caption.maxLength
      case 'tiktok':
        return TIKTOK_RESTRICTIONS.caption.maxLength
      case 'youtube':
        return YOUTUBE_RESTRICTIONS.metadata.description.maxLength
      case 'facebook':
        return FACEBOOK_RESTRICTIONS.caption.maxLength
      case 'meta-ads':
        return 2200
      default:
        return INSTAGRAM_RESTRICTIONS.caption.maxLength
    }
  }

  const maxLength = getMaxLength()
  const titleMaxLength = YOUTUBE_RESTRICTIONS.metadata.title.maxLength

  return (
    <Card className="relative h-full">
      {selectedCaptionPlatform === 'youtube' && onTitleChange && (
        <div className="mb-1">
          <input
            type="text"
            value={title || ''}
            onChange={(e) => onTitleChange(e.target.value)}
            readOnly={readOnly}
            placeholder="YouTube video title"
            maxLength={titleMaxLength}
            className="placeholder:text-muted-foreground w-full bg-transparent p-0 text-sm font-semibold outline-none"
          />
          <div className="text-muted-foreground flex justify-between border-b pb-1 text-xs">
            <span>Title</span>
            <span>
              {(title || '').length}/{titleMaxLength}
            </span>
          </div>
        </div>
      )}

      <Textarea
        ref={textareaRef ?? undefined}
        value={caption}
        onChange={(e) => onCaptionChange(e.target.value)}
        readOnly={readOnly}
        className={`resize-none whitespace-pre-wrap border-none p-0 pb-4 text-sm ${minHeight}`}
        placeholder={`Write your ${selectedCaptionPlatform} ${selectedCaptionPlatform === 'youtube' ? 'description' : 'caption'} here...`}
      />

      <div className="text-muted-foreground flex w-full justify-between text-xs">
        <div className="flex items-center justify-end">
          {readOnly ? (
            <div className="flex items-center gap-1">
              <Check className="h-4 w-4" />
              published — duplicate the workspace to edit
            </div>
          ) : isSaving ? (
            <div className="flex items-center gap-1">
              <Loader2Icon className="h-4 w-4 animate-spin" />
              saving...
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <Check className="h-4 w-4" />
              saved
            </div>
          )}
        </div>
        {caption.length}/{maxLength}
      </div>
    </Card>
  )
}
